"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CaliforniaRules = exports.CA_THRESHOLDS = void 0;
const browser_1 = require("encamp-prisma/generated/browser");
const isNonEmpty_1 = require("encamp-shared/dist/utils/isNonEmpty");
const types_1 = require("../types");
const _FEDERAL_1 = require("./_FEDERAL");
const _stateRules_1 = require("./_stateRules");
/**
 * California (DTSC) accumulation limits, 22 CCR division 4.5 chapter 12.
 */
const CA_STATE_STATUS_DOMAIN = [
    "LARGE_QUANTITY_GENERATOR",
    "SMALL_QUANTITY_GENERATOR",
    "VERY_SMALL_QUANTITY_GENERATOR", // TODO - we should remove this, but only after confirming that there are safeguards against it ever being saved to the db for CA
];
const CA_SATELLITE_SOURCE = { jurisdiction: "CA", citation: "22 CCR 66262.15(a)" };
/**
 * Federal rules under California's regulations, plus its standing one-year
 * satellite limit, which federal 262.15 has no counterpart for.
 */
exports.CA_THRESHOLDS = {
    centralTimeLimit: {
        ..._FEDERAL_1.FEDERAL_THRESHOLDS.centralTimeLimit,
        source: { jurisdiction: "CA", citation: "22 CCR 66262.16, 66262.17" },
    },
    standingSatelliteYear: {
        id: "standingSatelliteYear",
        scope: "CONTAINER",
        kind: "DAYS_SINCE",
        name: "Satellite accumulation deadline",
        source: { jurisdiction: "CA", citation: "22 CCR 66262.15(a)(9)" },
        consequences: [{ kind: "COMPLIANCE_NOT_RECORDED", actionToComply: "MOVE_TO_CENTRAL" }],
        basis: {},
        from: "ACCUMULATION_START",
        value: 365,
        warnDays: types_1.APPROACHING_WINDOW_DAYS,
    },
    /**
     * Chapter 23's one-year limit, from when the waste was generated or received
     * wherever it sits. 66273.1(b) supplies chapter 23's standards in lieu of
     * chapters 10 through 16, so chapter 12's satellite caps never sum it.
     */
    universalWasteTimeLimit: {
        ..._FEDERAL_1.FEDERAL_THRESHOLDS.universalWasteTimeLimit,
        source: { jurisdiction: "CA", citation: "22 CCR 66261.9, 66273.1(b), 66273.35(a)" },
    },
    capNonAcuteVolume: { ..._FEDERAL_1.FEDERAL_THRESHOLDS.capNonAcuteVolume, source: CA_SATELLITE_SOURCE },
    capAcuteVolume: { ..._FEDERAL_1.FEDERAL_THRESHOLDS.capAcuteVolume, source: CA_SATELLITE_SOURCE },
    capAcuteMass: { ..._FEDERAL_1.FEDERAL_THRESHOLDS.capAcuteMass, source: CA_SATELLITE_SOURCE },
    satelliteToCentralMoveWindow: {
        ..._FEDERAL_1.FEDERAL_THRESHOLDS.satelliteToCentralMoveWindow,
        source: CA_SATELLITE_SOURCE,
    },
};
/** California (DTSC) accumulation limits, 22 CCR division 4.5 chapter 12. */
class CaliforniaRules extends _stateRules_1.StateRules {
    constructor() {
        super(...arguments);
        this.stateStatusDomain = CA_STATE_STATUS_DOMAIN;
        this.adoptsEpisodicEvents = false;
    }
    /**
     * California's own on-site quantity conditions are not modeled: the
     * thresholds are the federal ones, keyed on the federal status alone.
     */
    facilityThresholds(facility) {
        return _FEDERAL_1.Federal.onSiteThresholds(facility);
    }
    centralTimeLimit(situation) {
        const effective = this.effectiveStatus(situation.facility);
        const basis = this.statusBasis(situation.facility);
        if (effective.value === undefined) {
            return {
                ...exports.CA_THRESHOLDS.centralTimeLimit,
                determination: "UNDETERMINED",
                missing: effective.missing,
                basis,
            };
        }
        if (effective.value === "LQG") {
            return {
                ...exports.CA_THRESHOLDS.centralTimeLimit,
                source: { jurisdiction: "CA", citation: "22 CCR 66262.17(a)" },
                determination: "KNOWN",
                basis,
                value: 90,
            };
        }
        const farTransport = situation.container.mustTransport200Miles;
        if (farTransport === undefined) {
            return {
                ...exports.CA_THRESHOLDS.centralTimeLimit,
                source: { jurisdiction: "CA", citation: "22 CCR 66262.16(b), (c)" },
                determination: "UNDETERMINED",
                missing: ["container.mustTransport200Miles"],
                basis,
            };
        }
        const source = {
            jurisdiction: "CA",
            citation: farTransport ? "22 CCR 66262.16(c)" : "22 CCR 66262.16(b)",
        };
        return {
            ...exports.CA_THRESHOLDS.centralTimeLimit,
            source,
            determination: "KNOWN",
            basis: { ...basis, container: { mustTransport200Miles: farTransport } },
            value: farTransport ? 270 : 180,
        };
    }
    /** California never adopted subpart L, so no container takes the episodic window. */
    containerThresholds(situation) {
        const program = this.governingProgram(situation.container);
        if (program.value === undefined) {
            const missing = [
                ...program.missing,
                ...(situation.area.type === undefined ? ["area.type"] : []),
            ];
            return [
                situation.area.type === "SATELLITE"
                    ? {
                        ...exports.CA_THRESHOLDS.standingSatelliteYear,
                        determination: "UNDETERMINED",
                        missing,
                    }
                    : {
                        ...exports.CA_THRESHOLDS.centralTimeLimit,
                        determination: "UNDETERMINED",
                        missing,
                        basis: {},
                    },
            ];
        }
        if (program.value === "UNIVERSAL_WASTE") {
            return [{ ...exports.CA_THRESHOLDS.universalWasteTimeLimit, determination: "KNOWN" }];
        }
        switch (situation.area.type) {
            case "CENTRAL":
                return [this.centralTimeLimit(situation)];
            case "SATELLITE": {
                const caps = this.satelliteCaps();
                return [
                    {
                        ...exports.CA_THRESHOLDS.standingSatelliteYear,
                        determination: "KNOWN",
                    },
                    ..._FEDERAL_1.Federal.satelliteMoveWindow(this, exports.CA_THRESHOLDS.satelliteToCentralMoveWindow, caps, situation.container),
                ];
            }
            case undefined:
                return [
                    {
                        ...exports.CA_THRESHOLDS.centralTimeLimit,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                ];
        }
    }
    /**
     * Whether chapter 23 governs a container as universal waste. Used oil and
     * pharmaceutical waste are ordinary chapter 12 waste here: California
     * regulates used oil as a non-RCRA state hazardous waste and never adopted
     * Subpart P.
     */
    governingProgram(container) {
        if (container.wasteKind === undefined) {
            return { missing: ["container.wasteKind"] };
        }
        return { value: container.wasteKind === "UNIVERSAL_WASTE" ? "UNIVERSAL_WASTE" : "RCRA" };
    }
    /**
     * The stricter of the federal status and California's own (22 CCR 66262.13).
     * LQG on either settles it; otherwise both must be known. Never VSQG:
     * California defines SQG with no lower bound, so a federal VSQG is a
     * California SQG.
     */
    effectiveStatus(facility) {
        const federal = facility.federalGeneratorStatus;
        const state = (0, _stateRules_1.narrowStateStatus)(facility, CA_STATE_STATUS_DOMAIN);
        if (federal === "LQG" || state === "LARGE_QUANTITY_GENERATOR") {
            return { value: "LQG" };
        }
        const missing = [
            ...(federal === undefined ? ["facility.federalGeneratorStatus"] : []),
            ...(state === undefined ? ["facility.stateGeneratorStatus"] : []),
        ];
        return (0, isNonEmpty_1.isNonEmpty)(missing) ? { missing } : { value: "SQG" };
    }
    /** The generator statuses the central clock read, as supplied. */
    statusBasis(facility) {
        const federal = facility.federalGeneratorStatus;
        const state = (0, _stateRules_1.narrowStateStatus)(facility, CA_STATE_STATUS_DOMAIN);
        if (federal === undefined && state === undefined) {
            return {};
        }
        return {
            facility: {
                ...(federal === undefined ? {} : { federalGeneratorStatus: federal }),
                ...(state === undefined ? {} : { stateGeneratorStatus: state }),
            },
        };
    }
    /**
     * Federal numbers under California's own regulation, reaching every tier:
     * no VSQG carve-out. They apply simultaneously; crossing one starts its
     * move window.
     */
    satelliteCaps() {
        return [
            {
                ...exports.CA_THRESHOLDS.capNonAcuteVolume,
                determination: "KNOWN",
                basis: {},
            },
            {
                ...exports.CA_THRESHOLDS.capAcuteVolume,
                determination: "KNOWN",
                basis: {},
            },
            {
                ...exports.CA_THRESHOLDS.capAcuteMass,
                determination: "KNOWN",
                basis: {},
            },
        ];
    }
    areaThresholds(situation) {
        switch (situation.area.type) {
            case "CENTRAL":
                return [];
            case "SATELLITE":
                return this.satelliteCaps();
            case undefined:
                return [
                    {
                        ...exports.CA_THRESHOLDS.capNonAcuteVolume,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                    {
                        ...exports.CA_THRESHOLDS.capAcuteVolume,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                    {
                        ...exports.CA_THRESHOLDS.capAcuteMass,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                ];
        }
    }
    accumulationCategory(container) {
        // STATE_REGULATED is non-acute pending the waste team's per-state decision.
        return container.classification === browser_1.WasteClassification.STATE_REGULATED
            ? { value: "NON_ACUTE" }
            : _FEDERAL_1.Federal.category(container);
    }
}
exports.CaliforniaRules = CaliforniaRules;
//# sourceMappingURL=CA.js.map